import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useCart } from "@/context/CartContext";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Trash2, Minus, Plus, ShoppingBag, Loader2 } from "lucide-react";
import axios from "axios";
import { API, fmtPrice } from "@/lib/api";
import { toast } from "sonner";

export const CartSheet = () => {
  const { items, remove, setQty, total, open, setOpen, count } = useCart();
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const checkout = async () => {
    setLoading(true);
    try {
      const res = await axios.post(`${API}/checkout/session`, {
        items: items.map((i) => ({ sku: i.sku, quantity: i.quantity })),
        origin_url: window.location.origin,
      });
      window.location.href = res.data.checkout_url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur lors du passage en caisse");
      setLoading(false);
    }
  };

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetContent className="flex w-full flex-col sm:max-w-md" data-testid="cart-sheet">
        <SheetHeader>
          <SheetTitle className="font-head text-xl">Votre panier ({count})</SheetTitle>
        </SheetHeader>

        {items.length === 0 ? (
          <div className="flex flex-1 flex-col items-center justify-center gap-3 text-slate-400">
            <ShoppingBag className="h-12 w-12" />
            <p>Votre panier est vide</p>
            <Button variant="outline" onClick={() => { setOpen(false); navigate("/catalogue"); }}>
              Découvrir nos produits
            </Button>
          </div>
        ) : (
          <>
            <div className="jc-scroll -mx-6 flex-1 overflow-y-auto px-6 py-4">
              {items.map((i) => (
                <div key={i.sku} data-testid={`cart-item-${i.sku}`} className="flex gap-3 border-b border-slate-100 py-4">
                  <div className="h-16 w-16 flex-shrink-0 overflow-hidden rounded-md border border-slate-200 bg-slate-50">
                    {i.image && <img src={i.image} alt={i.name} className="h-full w-full object-contain p-1" />}
                  </div>
                  <div className="flex-1">
                    <p className="line-clamp-2 text-sm font-medium text-slate-800">{i.name}</p>
                    <p className="mt-1 text-sm font-bold text-blue-700">{fmtPrice(i.price)}</p>
                    <div className="mt-2 flex items-center gap-2">
                      <button onClick={() => setQty(i.sku, i.quantity - 1)} className="rounded-md border border-slate-200 p-1 hover:bg-slate-50" data-testid={`qty-minus-${i.sku}`}>
                        <Minus className="h-3.5 w-3.5" />
                      </button>
                      <span className="w-6 text-center text-sm">{i.quantity}</span>
                      <button onClick={() => setQty(i.sku, i.quantity + 1)} className="rounded-md border border-slate-200 p-1 hover:bg-slate-50" data-testid={`qty-plus-${i.sku}`}>
                        <Plus className="h-3.5 w-3.5" />
                      </button>
                      <button onClick={() => remove(i.sku)} className="ml-auto text-slate-400 hover:text-red-600" data-testid={`remove-${i.sku}`}>
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="border-t border-slate-200 pt-4">
              <div className="mb-1 flex items-center justify-between">
                <span className="text-slate-600">Total TTC</span>
                <span className="font-head text-2xl font-extrabold text-slate-900" data-testid="cart-total">{fmtPrice(total)}</span>
              </div>
              <p className="mb-4 text-xs text-slate-400">Livraison calculée à l'étape suivante.</p>
              <Button
                data-testid="checkout-button"
                disabled={loading}
                onClick={checkout}
                className="w-full rounded-full bg-blue-600 py-6 text-base text-white hover:bg-blue-700"
              >
                {loading ? <Loader2 className="mr-2 h-5 w-5 animate-spin" /> : null}
                Passer à la caisse
              </Button>
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
};
